import { Link } from "react-router";
import type { JourneyOverviewPayload, OverviewMetrics } from "@/lib/journey-overview";
import { executionQuery, type OverviewWindow } from "@/lib/journey-overview-format";

type Props = {
  metrics: OverviewMetrics;
  window: OverviewWindow;
  executionBase: string;
  upcomingPeople?: number;
};

function MetricCard({
  title,
  value,
  hint,
  to,
}: {
  title: string;
  value: string | number;
  hint: string;
  to?: string;
}) {
  const inner = (
    <>
      <p className="text-xs font-medium text-muted-foreground">{title}</p>
      <p className="mt-1 text-2xl font-semibold tabular-nums tracking-tight text-foreground">{value}</p>
      <p className="mt-1 text-xs text-muted-foreground">{hint}</p>
    </>
  );
  if (to) {
    return (
      <Link
        to={to}
        className="block rounded-lg border border-border bg-background px-4 py-3 transition-colors hover:bg-muted/40"
      >
        {inner}
      </Link>
    );
  }
  return <div className="rounded-lg border border-border bg-background px-4 py-3">{inner}</div>;
}

/** 两段数字卡：企微/客户池 + 旅程执行（无「今日数字」壳） */
export function OverviewMetricsStrip({
  metrics,
  window,
  executionBase,
  upcomingPeople = 0,
}: Props) {
  const poolHint = window === "today" ? "今日" : "本窗";
  const next = metrics.next_slot;
  const voidedPeople = (metrics.voided?.people ?? 0) + (metrics.cancelled?.people ?? 0);
  const voidedBatches = (metrics.voided?.batches ?? 0) + (metrics.cancelled?.batches ?? 0);
  const exec = (status: string) => `${executionBase}${executionQuery(status)}`;

  return (
    <div className="space-y-6">
      <section>
        <h2 className="mb-3 text-sm font-medium text-foreground">企微 / 客户池</h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <MetricCard title={`${poolHint}新增`} value={metrics.new_contacts} hint="本地首次入库" />
          <MetricCard title={`${poolHint}流失`} value={metrics.churned_contacts} hint="跟进人删光" />
          <MetricCard
            title="已发送人数"
            value={metrics.sent_people}
            hint={window === "today" ? "今日去重客户" : "本窗去重客户"}
            to={exec("sent")}
          />
          <MetricCard
            title="下一档预计"
            value={next.people}
            hint={next.label || next.slot_key}
            to={exec("upcoming")}
          />
        </div>
      </section>
      <section>
        <h2 className="mb-3 text-sm font-medium text-foreground">旅程执行</h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <MetricCard
            title="待企微确认"
            value={metrics.pending_confirm.people}
            hint={`须在企微点发送 · 共 ${metrics.pending_confirm.batches} 批`}
            to={exec("pending_confirm")}
          />
          <MetricCard
            title="即将执行"
            value={upcomingPeople}
            hint="未来档计划人数"
            to={exec("upcoming")}
          />
          <MetricCard
            title="出站失败"
            value={metrics.failed.people}
            hint={`真正失败 · 共 ${metrics.failed.batches} 批`}
            to={exec("failed")}
          />
          <MetricCard
            title="已作废 / 已取消"
            value={voidedPeople}
            hint={`运营停单 · 共 ${voidedBatches} 批`}
            to={exec("voided")}
          />
        </div>
      </section>
    </div>
  );
}

export function upcomingPeopleFromBoard(
  board: JourneyOverviewPayload["board"] | undefined,
): number {
  return (board?.upcoming ?? []).reduce((sum, row) => sum + (row.people_count || 0), 0);
}
